import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  useApiAdminchangelogsIdPatch,
  useApiAdminchangelogsPost
} from '@r/api/generated/client.ts';
import type { ChangelogChangelogReadModel } from '@r/api/generated/client.schemas.ts';
import { apiAdminchangelogsPostBody } from '@r/api/generated-zod/index.ts';
import { invalidateChangelogQueries } from '@r/components/changelogs/invalidate-changelog-queries.ts';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useUploadFile } from '@r/hooks/use-upload-file.ts';
import { Button } from '@ui/button.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { RichTextEditor } from '@ui/rich-text-editor.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';

const TAG_OPTIONS = ['feature', 'improvement', 'bugfix'] as const;
const TARGET_OPTIONS = ['admins', 'sales', 'users'] as const;

/**
 * Étend le schéma généré par l’API : messages de validation traduits et champ média local
 * (fichier à uploader) à la place de l’IRI attendue côté backend.
 */
const getChangelogFormSchema = (t: ReturnType<typeof useTranslation>['t']) =>
  apiAdminchangelogsPostBody.extend({
    title: z.string().trim().min(1, t('fiddy.admin.changelog.form.title.required')),
    description: z.string().trim().min(1, t('fiddy.admin.changelog.form.description.required')),
    media: z.array(z.instanceof(File)).max(1).optional()
  });

type ChangelogFormValues = z.infer<ReturnType<typeof getChangelogFormSchema>>;

interface ChangelogFormProps {
  onSuccess: () => void;
  changelog?: ChangelogChangelogReadModel | null;
}

/**
 * Affiche le formulaire de création ou d’édition d’une entrée de changelog.
 * La présence d’un changelog détermine si le formulaire appelle le POST ou le PATCH.
 */
export function ChangelogForm({ onSuccess, changelog }: ChangelogFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const isEditing = Boolean(changelog);
  const formSchema = getChangelogFormSchema(t);

  const { onUpload, progresses, isUploading } = useUploadFile({ mediaType: 'assets' });
  const [removeExistingMedia, setRemoveExistingMedia] = useState(false);

  const form = useForm<ChangelogFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      title: changelog?.title ?? '',
      tag: changelog?.tag,
      target: changelog?.target,
      description: changelog?.description ?? '',
      media: []
    }
  });

  const stagedMedia = form.watch('media') ?? [];
  const existingMediaUri = changelog?.media?.pathUri ?? null;
  const showExistingMedia =
    Boolean(existingMediaUri) && !removeExistingMedia && stagedMedia.length === 0;

  /**
   * Rafraîchit la liste des changelogs puis ferme et réinitialise le formulaire.
   */
  const handleSuccess = async () => {
    await invalidateChangelogQueries(queryClient);
    toast.success(
      t(isEditing ? 'fiddy.admin.changelog.edit.success' : 'fiddy.admin.changelog.create.success')
    );
    form.reset();
    setRemoveExistingMedia(false);
    onSuccess();
  };

  const createChangelog = useApiAdminchangelogsPost({
    mutation: {
      onSuccess: handleSuccess,
      onError: (error) => {
        showOrvalErrorToast(error, t('fiddy.admin.changelog.create.error'));
      }
    }
  });

  const updateChangelog = useApiAdminchangelogsIdPatch({
    mutation: {
      onSuccess: handleSuccess,
      onError: (error) => {
        showOrvalErrorToast(error, t('fiddy.admin.changelog.edit.error'));
      }
    }
  });

  /**
   * Upload l’éventuel média puis envoie la création ou la modification.
   * `media` n’est transmis que s’il change (nouveau fichier ou suppression explicite),
   * l’API ne renvoyant pas l’IRI du média existant.
   */
  const onSubmit = async (values: ChangelogFormValues) => {
    const files = values.media ?? [];
    let media: string | null | undefined;

    if (files.length > 0) {
      const uploaded = await onUpload(files);
      const iri = uploaded?.[0]?.iri;
      if (!iri) return;
      media = iri;
    } else if (removeExistingMedia) {
      media = null;
    }

    const base = {
      title: values.title,
      tag: values.tag,
      description: values.description,
      target: values.target
    };
    const data = media === undefined ? base : { ...base, media };

    if (changelog) {
      updateChangelog.mutate({ id: String(changelog.id), data });
      return;
    }

    createChangelog.mutate({ data });
  };

  const isPending = createChangelog.isPending || updateChangelog.isPending || isUploading;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.changelog.form.title.label')} *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('fiddy.admin.changelog.form.title.placeholder')}
                  error={Boolean(fieldState.error)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-6 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="tag"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fiddy.admin.changelog.form.tag.label')} *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('fiddy.admin.changelog.form.tag.placeholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TAG_OPTIONS.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {t(`changelog.tag.${tag}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="target"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fiddy.admin.changelog.form.target.label')} *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t('fiddy.admin.changelog.form.target.placeholder')}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TARGET_OPTIONS.map((target) => (
                      <SelectItem key={target} value={target}>
                        {t(`fiddy.admin.changelog.target.${target}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.changelog.form.description.label')} *</FormLabel>
              <FormControl>
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={Boolean(fieldState.error)}
                  ariaLabel={t('fiddy.admin.changelog.form.description.label')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="media"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.changelog.form.media.label')}</FormLabel>
              <FileUploader
                value={field.value ?? []}
                onValueChange={field.onChange}
                maxFiles={1}
                maxSize={2 * 1024 * 1024}
                accept={{ 'image/*': [] }}
                progresses={progresses}
                disabled={isUploading}
                uploadedFiles={
                  showExistingMedia && existingMediaUri
                    ? [
                        {
                          name: t('fiddy.admin.changelog.form.media.current'),
                          type: 'image',
                          iri: '',
                          pathUri: existingMediaUri
                        }
                      ]
                    : []
                }
                onUploadedFileRemove={() => setRemoveExistingMedia(true)}
              />
              <p className="text-xs text-muted-foreground">
                {t('fiddy.admin.changelog.form.media.hint')}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-center border-t pt-5">
          <Button
            type="submit"
            className="w-full sm:max-w-72"
            loading={isPending}
            disabled={!form.formState.isValid}
          >
            {t(
              isEditing
                ? 'fiddy.admin.changelog.form.edit.submit'
                : 'fiddy.admin.changelog.form.create.submit'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
